import React from "react";
import StatusBadge from "./StatusBadge";
import { AlertCircle, Edit3, Clock, CheckCircle, Zap, Trash2 } from "lucide-react";

const SubmissionCard = ({ submission, onEditResubmit, onUpdateAndResubmit, onWithdraw }) => {
  const {
    millName,
    grade,
    season,
    rateExcl,
    rateIncl,
    qty,
    paymentDate,
    liftingDate,
    status,
    isExpired,
    submittedAt,
    approvedAt,
    approvedBy,
    rejectionReason,
  } = submission;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d?.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d?.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden" style={{ borderColor: "#f3e8d0" }}>
      {/* Top accent line */}
      <div
        className="h-1 w-full"
        style={{
          background: isExpired ? "#9a3412"
            : status === "live" ? "#0f766e"
              : status === "rejected" ? "#b91c1c" : "#f59e0b",
        }}
      />

      <div className="p-4 sm:p-5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-[#1f2937] truncate">
              {millName}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {grade} &bull; {season}
            </p>
          </div>
          <StatusBadge status={isExpired ? "expired" : status} />
        </div>

        {/* Rate & Qty grid — rate_excl_gst is the primary figure (it's what feeds
            quotas.price_per_ton downstream); rate_incl_gst is a small secondary
            annotation underneath it, never an equal-weight figure of its own. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <div className="rounded-lg p-2.5" style={{ background: "#faf7f2" }}>
            <p className="text-xs mb-0.5" style={{ color: "#52606d" }}>Rate</p>
            <p className="text-sm font-semibold font-mono text-[#1f2937]">
              ₹{rateExcl?.toLocaleString("en-IN")}
              <span className="font-sans font-normal text-[10px] ml-1" style={{ color: "#92400e" }}>excl. GST</span>
            </p>
            {rateIncl != null && (
              <p className="text-[10px] font-mono mt-0.5" style={{ color: "#52606d" }}>
                ₹{rateIncl?.toLocaleString("en-IN")} incl. GST
              </p>
            )}
          </div>
          <div className="rounded-lg p-2.5" style={{ background: "#faf7f2" }}>
            <p className="text-xs mb-0.5" style={{ color: "#52606d" }}>Qty (MT)</p>
            <p className="text-sm font-semibold font-mono text-[#1f2937]">{qty}</p>
          </div>
          <div className="rounded-lg p-2.5" style={{ background: "#faf7f2" }}>
            <p className="text-xs mb-0.5" style={{ color: "#52606d" }}>Payment Date</p>
            <p className="text-sm font-semibold text-[#1f2937]">
              {formatDate(paymentDate)}
            </p>
          </div>
        </div>

        {/* Lifting date + timestamp row — keyed off this card's OWN status, not the
            currently-selected tab, so every card reads correctly in the "All" view. */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs" style={{ color: "#52606d" }}>
          <span>
            Lifting:{" "}
            <span className="font-medium text-[#1f2937]">
              {formatDate(liftingDate)}
            </span>
          </span>

          {status === "live" ? (
            <span className="flex items-center gap-1 font-medium" style={{ color: "#0f766e" }}>
              <CheckCircle size={12} />
              Live since {formatDateTime(approvedAt)}
              {approvedBy ? ` · approved by ${approvedBy}` : ""}
            </span>
          ) : (
            <span className="flex items-center gap-1" style={{ color: "#52606d" }}>
              <Clock size={12} />
              Submitted {formatDateTime(submittedAt)}
            </span>
          )}
        </div>

        {/* Live: sync confirmation — real and unconditional. If status is 'live' the
            trigger that syncs into factories/quotas has already fired synchronously
            on approval, so this never needs (or gets) a separate check. Suppressed
            once expired — still technically true, but "Synced" next to an "Expired"
            badge reads as contradictory, and the expired notice below replaces it. */}
        {status === "live" && !isExpired && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md" style={{ background: "#e3f2ef", color: "#0b5c54" }}>
            <Zap size={11} />
            Synced to Mandi &amp; WhatsApp
          </div>
        )}

        {/* Expired: lifting date passed with stock still unsold. No forced action —
            stays visible as information until someone submits a fresh rate for this
            same mill+grade (which then supersedes it and it drops out of the Expired
            tab on its own). "Update & Resubmit" deep-links into Submit Rates with the
            mill pre-selected, rather than any in-place edit here. */}
        {isExpired && (
          <div className="mt-3 space-y-2">
            <div className="flex items-start gap-2 rounded-lg px-3 py-2.5" style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}>
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: "#9a3412" }} />
              <p className="text-xs" style={{ color: "#7c2d12" }}>
                Lifting date has passed with stock still unsold. Update the rate to republish, or leave as-is.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onUpdateAndResubmit?.(submission)}
                className="flex items-center gap-2 flex-1 justify-center px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors duration-150"
                style={{ background: "#9a3412" }}
              >
                <Edit3 size={14} />
                Update &amp; Resubmit
              </button>
              <button
                onClick={() => onWithdraw?.(submission)}
                title="Withdraw this offer — the mill itself is not affected"
                className="flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg border transition-colors duration-150"
                style={{ color: "#9a3412", borderColor: "#fed7aa", background: "#fff" }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Pending note */}
        {status === "pending" && (
          <div className="mt-3 flex items-center gap-2 text-xs rounded-lg px-3 py-2" style={{ color: "#92400e", background: "#fdecd3", border: "1px solid #f3e8d0" }}>
            <Clock size={12} className="flex-shrink-0" />
            Awaiting approval from Quota Manager / Admin
          </div>
        )}

        {/* Rejection reason + Submit corrected rate — always a fresh INSERT, never an
            in-place edit: officers have no UPDATE policy on mill_price_quotes, only
            INSERT and SELECT-own, so a corrected rate can only ever be a new row. */}
        {status === "rejected" && rejectionReason && (
          <div className="mt-3 space-y-2">
            <div className="flex items-start gap-2 rounded-lg px-3 py-2.5" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
              <AlertCircle
                size={14}
                className="flex-shrink-0 mt-0.5"
                style={{ color: "#b91c1c" }}
              />
              <div>
                <p className="text-xs font-semibold mb-0.5" style={{ color: "#b91c1c" }}>
                  Rejection Reason
                </p>
                <p className="text-xs" style={{ color: "#7f1d1d" }}>{rejectionReason}</p>
              </div>
            </div>
            <button
              onClick={() => onEditResubmit?.(submission)}
              className="flex items-center gap-2 w-full justify-center px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors duration-150"
              style={{ background: "#d97706" }}
            >
              <Edit3 size={14} />
              Submit Corrected Rate
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionCard;
